import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { IconButton, Box, Typography } from '@mui/material'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ReactCrop, { Crop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { styled } from '@mui/system'

const StoryEditor = forwardRef(
  (
    {
      image,
      setImage,
      imgDimensions,
      setImgDimensions,
      setCropped
    }: {
      image: File
      setImage: (image: File | null) => void
      imgDimensions: { width: number; height: number }
      setImgDimensions: (dim: { width: number; height: number }) => void
      setCropped: (crop: boolean) => void
    },
    ref
  ) => {
    const [crop, setCrop] = useState<Crop>({
      unit: '%' as const,
      x: 0,
      y: 0,
      width: 100,
      height: 100
    })
    const [src, setSrc] = useState<string | null>(null)
    const [rotation, setRotation] = useState<number>(0)
    const [objectFit, setObjectFit] = useState<'cover' | 'contain'>('cover')
    const [stretch, setStretch] = useState<'width' | 'height'>(null)
    const imageRef = useRef<HTMLImageElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const handleImageLoad = () => {
      if (imageRef.current) {
        const naturalWidth = imageRef.current.naturalWidth
        const naturalHeight = imageRef.current.naturalHeight
        const displayedWidth = containerRef.current.clientWidth
        const displayedHeight = containerRef.current.clientHeight

        if (naturalWidth > displayedWidth || naturalHeight > displayedHeight) {
          setObjectFit('contain')
        } else {
          setObjectFit('cover')
        }

        if (naturalHeight < displayedHeight && naturalHeight > naturalWidth) {
          setStretch('height')
        } else if (naturalWidth < displayedWidth && naturalWidth > naturalHeight) {
          setStretch('width')
        }

        if (displayedWidth !== imgDimensions.width || displayedHeight !== imgDimensions.height) {
          // Actualiza las dimensiones mostradas
          setImgDimensions({
            width: displayedWidth,
            height: displayedHeight
          })
        }
      }
    }

    useEffect(() => {
      if (!imageRef.current) return
      const imgWidth = imageRef.current.clientWidth
      const imgHeight = imageRef.current.clientHeight
      if (crop.unit === '%') {
        setCropped(false)
      } else {
        const isCropped = crop.width !== imgWidth || crop.height !== imgHeight || crop.x > 0 || crop.y > 0
        setCropped(isCropped)
      }
    }, [crop])

    useEffect(() => {
      const reader = new FileReader()
      reader.onloadend = () => {
        setSrc(reader.result as string)
        setCrop({
          unit: '%',
          x: 0,
          y: 0,
          width: 100,
          height: 100
        })
      }
      if (image) {
        reader.readAsDataURL(image)
      }
    }, [image])

    const getRotatedImage = (img: HTMLImageElement, degree: number): Promise<Blob | null> => {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const radians = (degree * Math.PI) / 180
        const width = img.naturalWidth
        const height = img.naturalHeight

        const newWidth = Math.abs(Math.cos(radians)) * width + Math.abs(Math.sin(radians)) * height
        const newHeight = Math.abs(Math.sin(radians)) * width + Math.abs(Math.cos(radians)) * height

        canvas.width = newWidth
        canvas.height = newHeight

        if (ctx) {
          ctx.translate(newWidth / 2, newHeight / 2)
          ctx.rotate(radians)
          ctx.drawImage(img, -width / 2, -height / 2)
        }

        canvas.toBlob((blob) => {
          resolve(blob)
        }, image.type)
      })
    }

    const getCroppedImg = async (): Promise<File | undefined> => {
      if (!src) return

      return new Promise((resolve) => {
        const img = new Image()
        img.src = src
        img.onload = () => {
          const scaleX = img.naturalWidth / imageRef.current!.clientWidth
          const scaleY = img.naturalHeight / imageRef.current!.clientHeight

          const pixelCrop = {
            x: Math.round(crop.x * scaleX),
            y: Math.round(crop.y * scaleY),
            width: Math.round(crop.width * scaleX),
            height: Math.round(crop.height * scaleY)
          }

          const finalCanvas = document.createElement('canvas')
          finalCanvas.width = pixelCrop.width
          finalCanvas.height = pixelCrop.height

          const finalCtx = finalCanvas.getContext('2d')!
          finalCtx.drawImage(
            img,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
          )

          finalCanvas.toBlob((blob) => {
            if (blob) {
              const croppedFile = new File([blob], image.name, { type: image.type })
              resolve(croppedFile)
            } else {
              resolve(undefined)
            }
          }, image.type)
        }
      })
    }

    const isSquare = () => {
      if (!imageRef.current) return false
      const { naturalWidth, naturalHeight } = imageRef.current
      return Math.abs(naturalWidth - naturalHeight) < 50
    }

    const handleRotate = async () => {
      if (!imageRef.current || !image) return

      const newRotation = (rotation + 90) % 360
      setRotation(newRotation)

      const rotatedBlob = await getRotatedImage(imageRef.current, 90)

      if (rotatedBlob) {
        const rotatedFile = new File([rotatedBlob], image.name, {
          type: image.type
        })
        const url = URL.createObjectURL(rotatedBlob)
        setSrc(url)

        // Update dimensions after rotation
        const img = new Image()
        img.src = url

        setImage(rotatedFile)
        setCrop({
          unit: '%',
          x: 0,
          y: 0,
          width: 100,
          height: 100
        })
      }
    }

    useImperativeHandle(ref, () => ({
      getCroppedImg
    }))

    return (
      <>
        <Box
          sx={{
            width: '100%',
            top: 0,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            position: 'absolute',
            backgroundColor: '#4D4D4D',
            color: 'white',
            overflow: 'hidden',
            zIndex: 1000
          }}
        >
          <IconButton
            sx={{
              color: 'white'
            }}
            onClick={() => {
              if (image) setImage(null)
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            style={{
              marginTop: '10px',
              marginBottom: '10px',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Recortá la foto
          </Typography>
          <div>
            <IconButton sx={{ color: 'white' }} onClick={handleRotate}>
              <SettingsBackupRestoreIcon />
            </IconButton>
          </div>
        </Box>
        {src && (
          <Box
            ref={containerRef}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={(c) => setCrop(c)}
              style={{
                overflow: 'hidden'
              }}
            >
              <img
                ref={imageRef}
                src={src}
                alt="Crop"
                style={{
                  width: '100%',
                  objectFit: objectFit,
                  display: 'block',
                  margin: '0 auto',
                  transition: 'transform 0.3s ease',
                  ...(objectFit === 'contain' && {
                    maxHeight: 'calc(100vh - 160px)'
                  }),
                  ...(objectFit === 'cover' && {
                    minHeight: stretch === 'height' ? containerRef.current?.clientHeight : 'auto',
                    minWidth: stretch === 'width' ? containerRef.current?.clientWidth : 'auto',
                    ...(isSquare() && {
                      minWidth: `${(containerRef.current?.clientWidth || 0) * 0.7}px`,
                      maxWidth: `${(containerRef.current?.clientWidth || 0) * 0.9}px`
                    })
                  })
                }}
                onLoad={handleImageLoad}
              />
            </ReactCrop>
          </Box>
        )}
      </>
    )
  }
)

export default StoryEditor
